import { Controller } from "@hotwired/stimulus"

// Show/hide toggle for the show page's "Prior cycle" slide comparison
// (card #2737, show-page follow-up round) -- swaps the slide artwork
// ITSELF in place with the client's previous cycle's equivalent slide,
// rather than flipping to a separate panel below/instead-of it. Jay's exact
// ask, after the first round's 4th flip-toggle segment missed the mark:
// "I just want the deck in place to change... a toggleable radio button up
// near the annotation icon" -- meaning the feedback-pins visibility toggle
// this button sits immediately beside (decks/_slide_toolbar.html.erb).
//
// Modeled directly on deck_slide_pins_toggle_controller.js -- same shape: a
// toolbar icon button that shows/hides sibling elements by DOM id, purely
// client-side, no server round-trip, no persistence. Default state is
// "showing the current slide" (this.showingPrior = false), the OPPOSITE
// starting polarity to the pins toggle (which defaults visible/"on") --
// here "on" means "showing the PRIOR slide", not the normal view.
//
// BOTH the current slide's turbo frame (decks/_slide_frame.html.erb,
// Deck.slide_frame_id) and the eagerly-rendered prior-cycle sibling
// (Deck.slide_prior_id, same file) already exist in the DOM at all times --
// eager + hidden, not a lazy turbo-frame fetch -- so toggling is instant,
// no loading state, and there is no separate stage-math to keep in sync:
// both render inside the SAME `.airdrop-slide-scale` wrapper
// (decks/_slide_list.html.erb), so swapping which one is visible can never
// move or resize the slide.
//
// The current-slide target is the turbo frame element, which already
// carries `style="display: block"` (decks/_slide_frame.html.erb's own
// comment explains why an unstyled turbo-frame needs that). Toggling it via
// `element.style.display` directly -- not a Tailwind `hidden` class --
// means this controller's own write always wins outright, with no
// specificity fight against that existing inline style. The prior-slide
// sibling has no such inline style, so a plain `hidden` class toggle is
// enough for it.
export default class extends Controller {
  static values = { currentId: String, priorId: String }

  connect() {
    this.showingPrior = false
  }

  toggle(event) {
    event.preventDefault()

    const current = document.getElementById(this.currentIdValue)
    const prior = document.getElementById(this.priorIdValue)
    if (!current || !prior) return

    this.showingPrior = !this.showingPrior
    current.style.display = this.showingPrior ? "none" : "block"
    prior.classList.toggle("hidden", !this.showingPrior)
    this._updateButtonState(event.currentTarget)
  }

  _updateButtonState(button) {
    button.classList.toggle("bg-gray-100", this.showingPrior)
    button.classList.toggle("hover:bg-gray-200", this.showingPrior)
    button.classList.toggle("hover:bg-gray-100", !this.showingPrior)
    button.classList.toggle("text-sapphire", this.showingPrior)
    button.classList.toggle("text-gray-600", !this.showingPrior)
    button.setAttribute("aria-pressed", String(this.showingPrior))
    button.title = this.showingPrior ? "Show current slide" : "Show prior cycle version"
  }
}
